/**
 * The permission-grants screen's own domain logic — Task T2-D — kept out of the
 * component so `grantStatusOf`, `buildGrantRequest` and the presentation tables are
 * testable without mounting anything, the same split `people/status.ts`,
 * `devices/fleet.ts` and `reports/schedule.ts` each keep for their own screen.
 *
 * ── Why status is computed, not read off the wire ────────────────────────────
 *
 * `PermissionGrant` carries `revoked_at` and `expires_at`, not a `status` field.
 * `grantStatusOf` is that predicate, transcribed once, so the table cell and the
 * default "active only" filter agree on what the three words mean — exactly the
 * reason `people/status.ts`'s own header gives for `employeeStatusOf`.
 *
 * A revoked row is reported `revoked` even past its own `expires_at`: revocation is
 * the more specific, deliberate fact (an administrator acted), and once it has
 * happened "it also expired" is no longer the interesting thing to say about the row.
 */

import type { MessageKey } from '../i18n/messages';
import type { IconName, Severity } from '../states/icons';
import {
  PERMISSION_CODES,
  type PermissionCode,
  type PermissionEffect,
  type PermissionGrant,
  type PermissionGrantRequest,
  type PermissionSubjectType,
} from '../api/permission-grants';

/* ─────────────────────────────────────────────────────── vocabulary grouping ── */

/** The four `PermissionCode` prefixes, in the order the "Mã quyền" column groups them. */
export const PERMISSION_GROUPS = ['workforce', 'recruiting', 'hr', 'payroll'] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

/** Every one of the 44 codes has exactly one of these four prefixes — see this file's own test. */
export function permissionGroupOf(code: PermissionCode): PermissionGroup {
  if (code.startsWith('workforce.')) return 'workforce';
  if (code.startsWith('recruiting.')) return 'recruiting';
  if (code.startsWith('hr.')) return 'hr';
  return 'payroll';
}

export const PERMISSION_GROUP_LABEL_KEY: Readonly<Record<PermissionGroup, MessageKey>> = {
  workforce: 'adminPermissions.group.workforce',
  recruiting: 'adminPermissions.group.recruiting',
  hr: 'adminPermissions.group.hr',
  payroll: 'adminPermissions.group.payroll',
};

/** `PERMISSION_CODES`, grouped for the "Cấp quyền" form's `<optgroup>`s, group by group. */
export const PERMISSION_CODES_BY_GROUP: Readonly<
  Record<PermissionGroup, readonly PermissionCode[]>
> = (() => {
  const byGroup: Record<PermissionGroup, PermissionCode[]> = {
    workforce: [],
    recruiting: [],
    hr: [],
    payroll: [],
  };
  for (const code of PERMISSION_CODES) byGroup[permissionGroupOf(code)].push(code);
  return byGroup;
})();

/**
 * One help sentence per code, for the "Cấp quyền" form's select — an exhaustive
 * `Record` rather than a `` `adminPermissions.code.${code}` `` template, for the
 * reason `DeviceBadges.tsx`'s own header gives about `STATUS_LABEL`: a 45th code
 * added to `PermissionCode` without an entry here must fail to **compile**, not
 * render a raw key at runtime.
 *
 * Neither `openapi.yaml` (one block description for the whole enum, not per value)
 * nor `docs/PERMISSION_MATRIX.md` (a generated table, no prose per code) publishes a
 * sentence for each of the 44 — so these are authored from the codes' own names and
 * `src/lib/permissions.ts` / `src/lib/hiring-permissions.ts`'s array comments, not
 * copied verbatim from either document.
 */
export const PERMISSION_CODE_DESCRIPTION_KEY: Readonly<Record<PermissionCode, MessageKey>> = {
  'workforce.view_self': 'adminPermissions.code.workforce.view_self',
  'workforce.view_department': 'adminPermissions.code.workforce.view_department',
  'workforce.view_workspace': 'adminPermissions.code.workforce.view_workspace',
  'workforce.manage_shifts': 'adminPermissions.code.workforce.manage_shifts',
  'workforce.manage_policies': 'adminPermissions.code.workforce.manage_policies',
  'workforce.manage_devices': 'adminPermissions.code.workforce.manage_devices',
  'workforce.view_apps': 'adminPermissions.code.workforce.view_apps',
  'workforce.view_websites': 'adminPermissions.code.workforce.view_websites',
  'workforce.view_file_events': 'adminPermissions.code.workforce.view_file_events',
  'workforce.view_usb_events': 'adminPermissions.code.workforce.view_usb_events',
  'workforce.view_screenshots': 'adminPermissions.code.workforce.view_screenshots',
  'workforce.export_reports': 'adminPermissions.code.workforce.export_reports',
  'workforce.manage_alerts': 'adminPermissions.code.workforce.manage_alerts',
  'workforce.resolve_incidents': 'adminPermissions.code.workforce.resolve_incidents',
  'workforce.view_audit_logs': 'adminPermissions.code.workforce.view_audit_logs',
  'workforce.manage_retention': 'adminPermissions.code.workforce.manage_retention',
  'workforce.use_ai_analytics': 'adminPermissions.code.workforce.use_ai_analytics',
  'recruiting.view_jobs': 'adminPermissions.code.recruiting.view_jobs',
  'recruiting.manage_jobs': 'adminPermissions.code.recruiting.manage_jobs',
  'recruiting.view_candidates': 'adminPermissions.code.recruiting.view_candidates',
  'recruiting.manage_candidates': 'adminPermissions.code.recruiting.manage_candidates',
  'recruiting.view_candidate_documents':
    'adminPermissions.code.recruiting.view_candidate_documents',
  'recruiting.submit_feedback': 'adminPermissions.code.recruiting.submit_feedback',
  'recruiting.manage_offers': 'adminPermissions.code.recruiting.manage_offers',
  'recruiting.hire': 'adminPermissions.code.recruiting.hire',
  'hr.view_employee_hr': 'adminPermissions.code.hr.view_employee_hr',
  'hr.view_employee_documents': 'adminPermissions.code.hr.view_employee_documents',
  'hr.manage_employee_documents': 'adminPermissions.code.hr.manage_employee_documents',
  'hr.manage_onboarding': 'adminPermissions.code.hr.manage_onboarding',
  'hr.view_leave': 'adminPermissions.code.hr.view_leave',
  'hr.request_leave': 'adminPermissions.code.hr.request_leave',
  'hr.approve_leave': 'adminPermissions.code.hr.approve_leave',
  'hr.manage_leave_types': 'adminPermissions.code.hr.manage_leave_types',
  'hr.view_reviews': 'adminPermissions.code.hr.view_reviews',
  'hr.manage_review_cycles': 'adminPermissions.code.hr.manage_review_cycles',
  'hr.submit_review': 'adminPermissions.code.hr.submit_review',
  'hr.view_own_review': 'adminPermissions.code.hr.view_own_review',
  'payroll.view_own_payslip': 'adminPermissions.code.payroll.view_own_payslip',
  'payroll.view_payroll': 'adminPermissions.code.payroll.view_payroll',
  'payroll.run_payroll': 'adminPermissions.code.payroll.run_payroll',
  'payroll.approve_payroll': 'adminPermissions.code.payroll.approve_payroll',
  'payroll.ceo_approve': 'adminPermissions.code.payroll.ceo_approve',
  'payroll.manage_templates': 'adminPermissions.code.payroll.manage_templates',
  'payroll.manage_regulations': 'adminPermissions.code.payroll.manage_regulations',
};

/* ──────────────────────────────────────────────────────────── subject labels ── */

export const SUBJECT_TYPE_LABEL_KEY: Readonly<Record<PermissionSubjectType, MessageKey>> = {
  employee: 'adminPermissions.subjectType.employee',
  role: 'adminPermissions.subjectType.role',
  department: 'adminPermissions.subjectType.department',
  job_title: 'adminPermissions.subjectType.jobTitle',
};

/** `wp_employees.role` — the three roles a `role`-subject grant may name. */
export const GRANT_ROLE_SUBJECTS = ['ceo', 'manager', 'employee'] as const;
export type GrantRoleSubject = (typeof GRANT_ROLE_SUBJECTS)[number];

/**
 * The same three `shell.role.*` labels `PeopleScreen.tsx` and `PeopleDrawer.tsx`
 * each keep their own local copy of, for a `role`-subject grant's `subject_key`.
 * Shared here rather than re-declared a third time, since both `GrantForm.tsx` and
 * `PermissionGrantsScreen.tsx` need it.
 */
export const ROLE_SUBJECT_LABEL_KEY: Readonly<Record<GrantRoleSubject, MessageKey>> = {
  ceo: 'shell.role.ceo',
  manager: 'shell.role.manager',
  employee: 'shell.role.employee',
};

/* ──────────────────────────────────────────────────────────────── effect ── */

export interface Presentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
}

/**
 * `allow` reads `low` — an ordinary, expected grant. `deny` reads `medium`: it is a
 * deliberate restriction an administrator chose, worth a second look, but not a
 * fault — the same "what does a reader have to do about it" test `DeviceBadges.tsx`
 * applies to its own severities.
 */
export const EFFECT_PRESENTATION: Readonly<Record<PermissionEffect, Presentation>> = {
  allow: { severity: 'low', icon: 'circle-check', labelKey: 'adminPermissions.effect.allow' },
  deny: { severity: 'medium', icon: 'ban', labelKey: 'adminPermissions.effect.deny' },
};

/* ──────────────────────────────────────────────────────────────── status ── */

export const GRANT_STATUSES = ['active', 'expired', 'revoked'] as const;
export type GrantStatus = (typeof GRANT_STATUSES)[number];

/**
 * `revoked_at` / `expires_at` vs. now, exactly as the brief's own three words name
 * them: `Đang hiệu lực` (active), `Hết hạn` (expired), `Đã thu hồi` (revoked).
 * `revoked_at` wins over `expires_at` when both would apply — see this file's header.
 */
export function grantStatusOf(
  grant: Pick<PermissionGrant, 'revoked_at' | 'expires_at'>,
  nowIso: string,
): GrantStatus {
  if (grant.revoked_at !== null) return 'revoked';
  if (
    grant.expires_at !== null &&
    new Date(grant.expires_at).getTime() <= new Date(nowIso).getTime()
  ) {
    return 'expired';
  }
  return 'active';
}

/**
 * `active` reads `low`, the same "good, unremarkable fact" severity
 * `EMPLOYEE_STATUS_PRESENTATION.active` uses. `expired` and `revoked` both read
 * `info` — settled lifecycle facts, not faults, the same treatment
 * `DeviceBadges.tsx`'s own `STATUS_SEVERITY` gives its own settled outcomes
 * (`revoked`, `uninstalled`). The two still take **different icons** — a clock
 * against a ban sign — so the two settled states remain distinguishable by
 * silhouette, never by colour alone.
 */
export const GRANT_STATUS_PRESENTATION: Readonly<Record<GrantStatus, Presentation>> = {
  active: { severity: 'low', icon: 'circle-check', labelKey: 'adminPermissions.status.active' },
  expired: { severity: 'info', icon: 'clock', labelKey: 'adminPermissions.status.expired' },
  revoked: { severity: 'info', icon: 'ban', labelKey: 'adminPermissions.status.revoked' },
};

/* ─────────────────────────────────────────────────────────── the create form ── */

/**
 * The "Cấp quyền" form's own draft shape — every field a plain string, exactly as
 * the controlled inputs that edit it hold them. `subjectKey` is whichever of the
 * four widgets `subjectType` selected: the `EmployeePicker`'s chosen id, the role
 * select's value, the department select's value, or the job-title free text.
 */
export interface GrantDraft {
  readonly subjectType: PermissionSubjectType;
  readonly subjectKey: string;
  readonly permissionCode: PermissionCode | '';
  readonly effect: PermissionEffect;
  /** Required in this UI even though optional in the API — see `GrantForm.tsx`. */
  readonly reason: string;
  /** An `<input type="datetime-local">` value, or `''` for no expiry. */
  readonly expiresAtLocal: string;
}

/** A `datetime-local` value → an ISO instant, or `undefined` when blank/unparseable. */
function toInstant(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * The exact `PermissionGrantRequest` body the form submits, or `null` while a
 * required field (subject key, permission code, or reason) is still blank — the
 * screen's own submit gate, so "can this be sent" and "what would be sent" cannot
 * disagree.
 *
 * `expires_at` is omitted rather than sent blank, the same "absent means the
 * server's own default" discipline `geo.ts`'s `OfficeLocationCreateRequest` keeps
 * for `radius_m` and `is_active`.
 */
export function buildGrantRequest(draft: GrantDraft): PermissionGrantRequest | null {
  const subjectKey = draft.subjectKey.trim();
  const reason = draft.reason.trim();
  if (draft.permissionCode === '' || subjectKey === '' || reason === '') return null;
  const expiresAt = toInstant(draft.expiresAtLocal);
  return {
    subject_type: draft.subjectType,
    subject_key: subjectKey,
    permission_code: draft.permissionCode,
    effect: draft.effect,
    reason,
    ...(expiresAt === undefined ? {} : { expires_at: expiresAt }),
  };
}

/* ─────────────────────────────────────────────────── created vs. replaced ── */

/**
 * The grant already on this exact key, if one exists — `(subject_type, subject_key,
 * permission_code)`, ignoring `revoked_at`, mirroring `readGrantByKey`
 * (`src/modules/permissions/repo.ts`) exactly: a revoked row on the same key is
 * still "one exists", because re-granting it is the server's own `200` "replaced"
 * case, not a `201`.
 *
 * `useApiMutation`'s `MutationResult` carries no HTTP status, so this is how the
 * screen tells a `201` "Đã cấp quyền" toast from a `200` "Đã cập nhật quyền" one:
 * checked against the list **as it stood before the submit**, which is the caller's
 * responsibility to snapshot — see `PermissionGrantsScreen.tsx`.
 */
export function existingGrantFor(
  grants: readonly PermissionGrant[],
  subjectType: PermissionSubjectType,
  subjectKey: string,
  permissionCode: PermissionCode,
): PermissionGrant | null {
  return (
    grants.find(
      (grant) =>
        grant.subject_type === subjectType &&
        grant.subject_key === subjectKey &&
        grant.permission_code === permissionCode,
    ) ?? null
  );
}
